import { useEffect, useState, useCallback } from 'react';
import {
  readEngagementTeam,
  addEngagementTeamMember,
  removeEngagementTeamMember,
  listFirmUsers,
} from './grcClient.js';

/**
 * EngagementTeamPanel
 *
 * Who from the firm can reach this client's engagement, and the controls to
 * change it (GACA-04 / GRC-B-010).
 *
 * Membership in grc_engagement_team_members is the real access-control
 * mechanism for the whole auditor side — every authorization predicate reads
 * it. Removing someone here takes effect on their very next request.
 *
 * Visible to everyone on the engagement (knowing who else is on your own job
 * is not privileged); the add/remove controls are server-gated to a firm-admin
 * or this engagement's lead auditor, and mirrored here so the buttons only
 * appear to someone who can actually use them.
 */

const ROLE_LABELS = {
  'lead-auditor': 'Lead auditor',
  'staff-auditor': 'Staff auditor',
  'quality-reviewer': 'Quality reviewer',
};

const ASSIGNABLE_ROLES = Object.keys(ROLE_LABELS);

/** The signed-in auditor, as auditor-login stored it: { id, displayName, email, role, firmId }. */
function storedSession() {
  try {
    return JSON.parse(localStorage.getItem('grc_auditor_session') || 'null');
  } catch {
    return null;
  }
}

export default function EngagementTeamPanel({ engagementId }) {
  const currentUser = storedSession();
  const firmId = currentUser?.firmId;

  const [members, setMembers] = useState([]);
  const [firmUsers, setFirmUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [addUserId, setAddUserId] = useState('');
  const [addRole, setAddRole] = useState('staff-auditor');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await readEngagementTeam(engagementId);
      setMembers(data.members || []);
    } catch (e) {
      setError(e.message || 'Could not load the engagement team');
    } finally {
      setLoading(false);
    }
  }, [engagementId]);

  useEffect(() => {
    load();
  }, [load]);

  // The firm roster is only needed to populate the "add" picker, and only a
  // firm-admin can read it — a failure here must not break the team list.
  useEffect(() => {
    if (!firmId) return;
    listFirmUsers(firmId)
      .then((d) => setFirmUsers(d.users || []))
      .catch(() => setFirmUsers([]));
  }, [firmId]);

  const myMembership = members.find((m) => m.userId === currentUser?.id);
  const canManage = currentUser?.role === 'firm-admin' || myMembership?.role === 'lead-auditor';

  const act = useCallback(
    async (fn) => {
      setBusy(true);
      setError('');
      try {
        await fn();
        await load();
      } catch (e) {
        setError(e.message || 'That change could not be applied');
      } finally {
        setBusy(false);
      }
    },
    [load]
  );

  const memberIds = new Set(members.map((m) => m.userId));
  const addable = firmUsers.filter((u) => u.isActive !== false && !memberIds.has(u.id));

  if (loading) {
    return <div style={{ color: 'var(--fg3)', fontSize: 13 }}>Loading team…</div>;
  }

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg)' }}>Engagement team</div>
        <p style={{ fontSize: 12, color: 'var(--fg3)', margin: '4px 0 0', lineHeight: 1.6 }}>
          Only these people can open this engagement or reach the client&rsquo;s workspace. Removing
          someone takes effect immediately, and the change is recorded in the client&rsquo;s own
          access log.
        </p>
      </div>

      {error && (
        <div
          style={{
            padding: '8px 12px',
            borderRadius: 8,
            marginBottom: 12,
            background: 'var(--danger-bg)',
            border: '1px solid var(--danger)',
            fontSize: 12,
            color: 'var(--danger)',
          }}
        >
          {error}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
        {members.map((m) => (
          <div
            key={m.id}
            className="card"
            style={{
              padding: '10px 14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)' }}>
                {m.displayName}
                {m.userId === currentUser?.id && (
                  <span style={{ color: 'var(--fg3)', fontWeight: 400 }}> — you</span>
                )}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--fg3)',
                  fontFamily: 'var(--font-mono)',
                  marginTop: 2,
                }}
              >
                {ROLE_LABELS[m.role] || m.role} · {m.email}
              </div>
            </div>

            {canManage && (
              <button
                type="button"
                className="btn btn-sm"
                disabled={busy || members.length <= 1}
                title={
                  members.length <= 1
                    ? 'This is the last person on the engagement — add a replacement first'
                    : `Remove ${m.displayName} from this engagement`
                }
                onClick={() =>
                  act(() => removeEngagementTeamMember({ engagementId, userId: m.userId }))
                }
              >
                Remove
              </button>
            )}
          </div>
        ))}
      </div>

      {canManage && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            className="input"
            style={{ maxWidth: 240 }}
            value={addUserId}
            onChange={(e) => setAddUserId(e.target.value)}
            disabled={busy || addable.length === 0}
          >
            <option value="">
              {addable.length ? 'Add someone from the firm…' : 'Nobody left to add'}
            </option>
            {addable.map((u) => (
              <option key={u.id} value={u.id}>
                {u.displayName} ({u.email})
              </option>
            ))}
          </select>

          <select
            className="input"
            style={{ maxWidth: 180 }}
            value={addRole}
            onChange={(e) => setAddRole(e.target.value)}
            disabled={busy}
          >
            {ASSIGNABLE_ROLES.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABELS[r]}
              </option>
            ))}
          </select>

          <button
            type="button"
            className="btn btn-sm btn-primary"
            disabled={busy || !addUserId}
            onClick={() =>
              act(async () => {
                await addEngagementTeamMember({ engagementId, userId: addUserId, role: addRole });
                setAddUserId('');
              })
            }
          >
            Add to engagement
          </button>
        </div>
      )}
    </div>
  );
}
